/**
 * API client configuration and error handling
 *
 * Single API backend:
 *   API_V2_URL — Neon-backed API (port 3002)
 *
 * Auth: cookie sessions only. The session cookie is sent via credentials:'include'.
 * CSRF: X-CSRF-Token header attached on state-changing requests (POST/PUT/PATCH/DELETE).
 */

import { getCsrfToken, fetchCsrfToken, isStateChangingMethod } from './csrf';

export const API_V2_URL = import.meta.env.VITE_PUBLIC_API_V2_URL || 'http://localhost:3002';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public retryable: boolean = false,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Error status handlers per integration contract:
 * - 401/403: Sign-in required
 * - 429/5xx: Retry
 * - Timeouts: Try again (preserve input)
 */
export function handleApiError(status: number, message?: string, code?: string): ApiError {
  switch (status) {
    case 401:
      return new ApiError('Sign-in required. Please authenticate.', status, false, code);
    case 403:
      return new ApiError(message || 'Access denied. You do not have permission.', status, false, code);
    case 429:
      return new ApiError('Too many requests. Please try again later.', status, true, code);
    default:
      if (status >= 500) {
        return new ApiError(message || 'Server error. Please retry.', status, true, code);
      }
      return new ApiError(message || 'An error occurred.', status, false, code);
  }
}

/**
 * Fetch wrapper for the Neon-backed API.
 * Adds JWT auth header, cookie credentials, CSRF token, 30s timeout,
 * and structured error handling. Auto-retries once on CSRF_INVALID.
 */
export async function apiV2Fetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const result = await _apiV2FetchInternal<T>(endpoint, options);

  // Auto-retry once on CSRF failure: refetch token and replay
  if (result.__csrfRetry) {
    await fetchCsrfToken();
    const retryResult = await _apiV2FetchInternal<T>(endpoint, options);
    if (retryResult.__csrfRetry) {
      // Still failing — surface the error
      throw handleApiError(403, 'Session expired, please refresh or log in again.');
    }
    return retryResult.data;
  }

  return result.data;
}

/** Internal fetch with CSRF retry signaling */
async function _apiV2FetchInternal<T>(
  endpoint: string,
  options?: RequestInit
): Promise<{ data: T; __csrfRetry?: boolean }> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  // Content-Type only when a body is actually sent — Fastify rejects an
  // empty body carrying 'application/json' (FST_ERR_CTP_EMPTY_JSON_BODY),
  // which broke body-less POSTs like export generate.
  const headers: Record<string, string> = {};
  if (options?.body != null) {
    headers['Content-Type'] = 'application/json';
  }

  // CSRF token — attach on state-changing requests
  const method = options?.method?.toUpperCase() || 'GET';
  if (isStateChangingMethod(method)) {
    const csrf = getCsrfToken();
    if (csrf) {
      headers['X-CSRF-Token'] = csrf;
    }
  }

  if (options?.headers) {
    Object.assign(headers, options.headers as Record<string, string>);
  }

  try {
    const response = await fetch(`${API_V2_URL}${endpoint}`, {
      ...options,
      signal: controller.signal,
      headers,
      credentials: 'include',
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      // Dispatch auth:logout on 401
      if (response.status === 401) {
        window.dispatchEvent(new Event('auth:logout'));
      }
      const errorData = await response.json().catch(() => ({}));

      // Signal CSRF retry if backend returns 403 CSRF_INVALID
      if (response.status === 403 && errorData.error === 'CSRF_INVALID') {
        return { data: {} as T, __csrfRetry: true };
      }

      throw handleApiError(response.status, errorData.error || errorData.message, errorData.code);
    }

    const text = await response.text();
    if (text.length === 0) return { data: {} as T };

    try {
      return { data: JSON.parse(text) as T };
    } catch {
      throw new ApiError('API returned invalid JSON.', 502, false);
    }
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof ApiError) throw error;
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError('Request timed out. Please try again.', 408, true);
    }
    throw new ApiError('Network error. Please check your connection.', 0, true);
  }
}
